// src/components/layout/Navbar.jsx
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { auth } from '@/services/firebase';
import { signOut } from 'firebase/auth';
import { ChevronDown, LogOut, User } from 'lucide-react';
import { StorageUtils, STORAGE_KEYS } from '@/utils/storage';
import { getUserFirstName } from '@/utils/user';

export function Navbar() {
  const { user, profile } = useAuth();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const handleLogout = async () => {
    try {
      // Clear all app storage on logout
      StorageUtils.clearAppStorage();
      await signOut(auth);
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  // Get cached profile and user's first name
  const cachedProfile = StorageUtils.getItem(STORAGE_KEYS.PROFILE, {
    defaultValue: {},
  });
  const firstName = getUserFirstName({ profile, user, cachedProfile });

  return (
    <nav className="sl-navbar bg-white shadow-sm">
      <div className="sl-navbar-container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="sl-navbar-content flex h-16 items-center justify-between">
          {/* Logo Section */}
          <div className="sl-navbar-brand flex flex-shrink-0 items-center">
            <Link
              to="/"
              className="sl-navbar-logo flex items-center space-x-1 text-xl font-bold text-slate-700">
              <img
                src="/img/logo.svg"
                alt="SourceLink Logo"
                className="h-6 w-6"
              />
              <span>SourceLink</span>
            </Link>
          </div>

          {/* User Profile Section */}
          <div className="sl-navbar-profile relative ml-3">
            <div>
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="sl-profile-button flex items-center rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-primary-400 focus:ring-offset-2">
                {user?.photoURL ? (
                  <img
                    className="sl-profile-avatar h-8 w-8 rounded-full"
                    src={user.photoURL}
                    alt={firstName || user.displayName}
                  />
                ) : (
                  <div className="sl-profile-avatar-placeholder flex h-8 w-8 items-center justify-center rounded-full bg-primary-100 text-primary-600">
                    <User size={20} />
                  </div>
                )}
                <span className="sl-profile-name ml-2 hidden text-sm font-medium text-gray-700 md:block">
                  {firstName || user?.displayName}
                </span>
                <ChevronDown className="ml-2 h-4 w-4 text-gray-400" />
              </button>
            </div>

            {/* Dropdown Menu */}
            {isMenuOpen && (
              <div className="sl-profile-dropdown absolute right-0 mt-2 w-48 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                <Link
                  to="/profile"
                  className="sl-profile-menu-item flex w-full items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                  <User className="mr-3 h-4 w-4" />
                  Profile
                </Link>
                <button
                  onClick={handleLogout}
                  className="sl-profile-menu-item flex w-full items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                  <LogOut className="mr-3 h-4 w-4" />
                  Sign out
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
}
